'use client';

import type { FactiiiRequirement, FactiiiTag, PinnedFactiii } from '@shared/all/domains/factiiis';
import type { Post } from '@shared/all/domains/posts';
import type { CrossPostPlatform } from '@shared/all/validators';
import type { UploadItem } from '@shared/utils/hooks/useUpload';
import type { QueryKey } from '@tanstack/react-query';
import { createContext, type Dispatch, type RefObject, type SetStateAction, useContext } from 'react';
export type DraftItemSelectHandler = {
  content: string;
  factiiiOpen: boolean;
  refDraftPostId: string;
  anonymousPost: boolean;
};
export type NewPostContextValue = {
  // Content
  title: string;
  setTitle: (value: string) => void;
  content: string;
  setContent: (value: string) => void;
  /** Over the server's limit: Post is disabled and the composer says why. */
  contentTooLong: boolean;
  rows: number;
  label: string;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => false | Promise<void>;

  // Uploads
  uploads: UploadItem[];
  uploadingKeys: string[];
  pendingFiles: {
    key: string;
    fileName?: string;
  }[];
  removeAttachmentHandler: (id: string) => void;
  cameraPhotos: {
    uri: string;
    factiiiId: number;
  }[];
  removeCameraPhotoHandler: (factiiiId: number) => void;

  // Factiii toggle
  factiiiOpen: boolean;
  setFactiiiOpen: Dispatch<SetStateAction<boolean>>;

  // DefaultFactiiiButtons
  myPostFactiiiCounts: FactiiiTag[];
  setMyPostFactiiiCounts: Dispatch<SetStateAction<FactiiiTag[]>>;
  requirementsMet: {
    tag: FactiiiTag;
    results: {
      completed: boolean;
      description: string;
      requirement: FactiiiRequirement;
    }[];
  }[];
  initialLocation?: {
    latitude: number;
    longitude: number;
  } | null;

  // Settings
  anonymousPost: boolean;
  setAnonymousPost: (anonymous: boolean) => void;
  privatePost: boolean;
  setPrivatePost: (privatePost: boolean) => void;
  refDraftPostId?: string;
  // Per-post cross-post selection; null = follow the account's auto defaults
  crossPostSelection: CrossPostPlatform[] | null;
  setCrossPostSelection: (platforms: CrossPostPlatform[] | null) => void;
  // Save the post to the schedule with these networks and open the Scheduled
  // posts page, where its time is shown and can be changed.
  schedulePost: (platforms: CrossPostPlatform[]) => void;

  // Toolbar actions
  onDraftsClick: () => void;
  onFilesSelected: (files: File[]) => void;

  // Submit
  submitHandler: () => void;
  saveDraft: () => void;
  forceSubmit: () => void;
  submitDisabled: boolean;
  buttonLoading: boolean;
  submitButtonText: string;

  // NewPostFactiii
  pinnedFactiii?: PinnedFactiii;
  queryKey: QueryKey;
  postId: string;
  isPrivate: boolean;
  isOpened: boolean;

  // Layout tweaks
  factiiiScrollable?: boolean;
  toolbarPt?: string;

  // Modal states
  closeHandler: (arg0?: boolean) => void;
  closeOrSaveDraft: () => void;
  moderationConfModalOpen: boolean;
  setModerationConfModalOpen: Dispatch<SetStateAction<boolean>>;
  moderationRequiredFactiiis: string[];
  saveDraftConfModalOpen: boolean;
  setSaveDraftConfModalOpen: Dispatch<SetStateAction<boolean>>;
  draftsListModalOpen: boolean;
  setDraftsListModalOpen: Dispatch<SetStateAction<boolean>>;
  draftItemSelectHandler: (data: DraftItemSelectHandler) => void;

  // Layout routing
  isReplyMode: boolean;
  isTopLevelReplyModal: boolean;
  inline?: boolean;
  isBoardPost?: boolean;
  spaceSlug?: string;
  parentPostId?: string;
  parentPost?: Post;
  parentIsPrivate?: boolean;
  uniqueId?: string;
  scrollAreaRef: RefObject<HTMLDivElement | null>;
};
export const NewPostContext = createContext<NewPostContextValue | null>(null);
export const useNewPostContext = () => {
  const ctx = useContext(NewPostContext);
  if (!ctx) {
    throw new Error("useNewPostContext must be used within NewPostProvider");
  }
  return ctx;
};